// /fame — the Hall of Fame: creators and songs ranked by their ratings (all time or this week).
// The ranking is a Bayesian average, so a single vote can't top it; the page says so.
import { useEffect, useState } from "react";
import { clock } from "../stream";
import { api } from "../api";
import { Link, useTitle } from "../lib/router";
import { fetchServerTime } from "../river/player";
import { formatPlayTime } from "../river/slots";
import { StarAverage } from "../ui/Stars";

const { slotStartMs } = clock;

interface Fame {
  creators: { nickname: string; average: number; count: number; songs: number; score: number }[];
  songs: {
    id: string;
    title: string | null;
    author: string;
    day: number;
    slot: number | null;
    average: number;
    count: number;
    score: number;
  }[];
}

export function FamePage() {
  useTitle("Hall of Fame");
  const [period, setPeriod] = useState<"all" | "week">("all");
  const [fame, setFame] = useState<Fame | null>(null);
  const [epoch, setEpoch] = useState<number | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setFame(null);
    api<Fame>("GET", `/api/fame?period=${period}`)
      .then(setFame)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [period]);
  useEffect(() => {
    void fetchServerTime().then((t) => setEpoch(t.epochMs));
  }, []);
  return (
    <main id="main" className="page fame-page">
      <h1>Hall of Fame</h1>
      <p className="muted">
        Listeners rate the songs they hear, 1 to 5 stars. Rankings start every song and creator from five imaginary
        three-star votes, so it takes several good ratings — not one — to rise to the top. Creators need at least three
        ratings to be listed.
      </p>
      <div className="segmented" role="radiogroup" aria-label="Period">
        {(
          [
            ["all", "All time"],
            ["week", "This week"],
          ] as const
        ).map(([v, l]) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={period === v}
            className={period === v ? "on" : ""}
            onClick={() => setPeriod(v)}
            data-testid={`fame-${v}`}
          >
            {l}
          </button>
        ))}
      </div>
      {error && <p role="alert">{error}</p>}
      {!fame ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="fame-grid">
          <section className="panel" aria-labelledby="fame-creators">
            <h2 id="fame-creators">Creators</h2>
            {fame.creators.length === 0 ? (
              <p className="muted">Nobody has enough ratings yet. Listen, and rate the songs you love.</p>
            ) : (
              <ol className="fame-list" data-testid="fame-creators">
                {fame.creators.map((c, i) => (
                  <li key={c.nickname}>
                    <span className={`fame-rank${i < 3 ? ` top${i + 1}` : ""}`}>{i + 1}</span>
                    <span className="fame-name">
                      <Link href={`/u/${encodeURIComponent(c.nickname)}`}>{c.nickname}</Link>
                      <span className="muted small">
                        {c.songs} song{c.songs === 1 ? "" : "s"}
                      </span>
                    </span>
                    <StarAverage average={c.average} count={c.count} />
                  </li>
                ))}
              </ol>
            )}
          </section>
          <section className="panel" aria-labelledby="fame-songs">
            <h2 id="fame-songs">Songs</h2>
            {fame.songs.length === 0 ? (
              <p className="muted">No rated songs yet.</p>
            ) : (
              <ol className="fame-list" data-testid="fame-songs">
                {fame.songs.map((s, i) => (
                  <li key={s.id}>
                    <span className={`fame-rank${i < 3 ? ` top${i + 1}` : ""}`}>{i + 1}</span>
                    <span className="fame-name">
                      <Link href={`/loop/${encodeURIComponent(s.id)}`}>{s.title ?? "Untitled song"}</Link>
                      <span className="muted small">
                        by <Link href={`/u/${encodeURIComponent(s.author)}`}>{s.author}</Link>
                        {epoch !== null && s.slot !== null && ` · ${formatPlayTime(slotStartMs(s.day, s.slot, epoch))}`}
                      </span>
                    </span>
                    <StarAverage average={s.average} count={s.count} />
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
